import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import SydButton from '../../../../../commonDesign/Button'
import Markup from '../../../../../atoms/Markup'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'
import PlanStatusIcon from '../badges/PlanStatusIcon'
import { detectMarkupType, isValueEmpty, resolveMetadataValue } from '../helpers'
import usePanelMaxHeight from '../hooks/usePanelMaxHeight'
import EmbeddedItemSupplemental from './EmbeddedItemSupplemental'
import DetailRow from './DetailRow'

const useStyles = makeStyles(() => ({
  panel: {
    minHeight: '300px',
    borderRadius: 'var(--border-radius-lg)',
    background: 'var(--color-neutral-100)',
    border: 'var(--border-neutral-thin)',
    overflow: 'hidden'
  },
  panelTitle: {
    borderBottom: 'var(--border-neutral-thin)',
    padding: 'var(--dim-2) var(--dim-4)',
    color: 'var(--color-neutral-800)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  panelContent: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: 'var(--dim-4)'
  },
  descriptionContent: {
    '& p': {
      margin: 0
    },
    '& * p': {
      marginTop: '1em'
    }
  },
  detailList: {
    '& .__detail-item': {
      display: 'flex',
      gap: 'var(--dim-4)',
      padding: 'var(--dim-2) 0',
      alignItems: 'center'
    },
    '& .__detail-item:not(:last-child)': {
      borderBottom: 'var(--border-neutral-thin)'
    },
    '& .__detail-item-header': {
      width: '120px',
      color: 'var(--color-neutral-800)',
      fontWeight: 'var(--font-weight-light)',
      flexShrink: 0
    },
    '& .__detail-item-content': {
      flex: 1,
      minWidth: 0,
      wordBreak: 'break-word'
    }
  },
  attentionBox: {
    background: 'color-mix(in srgb, var(--color-decline-500) 10%, transparent)',
    padding: 'var(--dim-3)',
    borderRadius: 'var(--border-radius-md)'
  }
}))

const sectionLabelStyle = {
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
}

function PlanItemDetailPanel ({ node, kind, category, metadataRows, statusStyleOverrides, taskComponent, onClose }) {
  const classes = useStyles()
  const { ref, maxHeight } = usePanelMaxHeight()
  const { formatter } = useFormattingContext()

  // For items the title bar shows the parent category name (legacy behaviour).
  // For categories it shows the category's own name so the user has context
  // about what they selected.
  const titleText = kind === 'category' ? (node?.name || '') : (category?.name || '')

  const renderedRows = (metadataRows ?? [])
    .filter(row => row && typeof row === 'object')
    .map(row => ({ row, value: resolveMetadataValue(node, row, formatter) }))
    .filter(({ row, value }) => !(row.hideWhenEmpty && isValueEmpty(value)))

  return (
    <div ref={ref} className={`${classes.panel} flex flex-col`} style={{ maxHeight: maxHeight ?? undefined }}>
      <div className={`${classes.panelTitle} flex items-center justify-between font-size-xs`} style={{ flexShrink: 0 }}>
        <span>{titleText}</span>
        <SydButton size='xs' variant='secondary' onClick={onClose}>Close</SydButton>
      </div>

      <div className={`${classes.panelContent} flex flex-col gap-md`}>
        <div className='flex flex-col gap-2'>
          <PlanStatusIcon status={node.status} size={30} statusStyleOverrides={statusStyleOverrides} />
          <div className='flex flex-col gap-xs'>
            <p className='font-size-lg font-medium m-0'>{node.name}</p>
            {!!node.description?.length && (
              <div className='font-size-sm font-light m-0' style={{ color: 'var(--text-color-subtle)' }}>
                <Markup className={classes.descriptionContent} value={node.description} type={detectMarkupType(node.description)} style={{ fontSize: 'inherit' }} />
              </div>
            )}
          </div>
        </div>

        {renderedRows.length > 0 ? (
          <div className={`${classes.detailList} flex flex-col font-size-sm`}>
            {renderedRows.map(({ row, value }) => (
              <DetailRow key={row.id ?? row.accessor ?? row.label} label={row.label} value={value} />
            ))}
          </div>
        ) : null}

        {node.metadata?.needsAttention ? (
          <div className={classes.attentionBox}>
            <div
              className='font-size-xs font-medium mb-1'
              style={{ color: 'var(--color-decline-500)', ...sectionLabelStyle }}
            >
              Needs Attention
            </div>
            {node.metadata.needsAttentionReason ? (
              <div className='font-size-sm'>{node.metadata.needsAttentionReason}</div>
            ) : null}
          </div>
        ) : null}

        {taskComponent?.blueprintCode && taskComponent?.presentationKey ? (
          <EmbeddedItemSupplemental item={node} taskComponent={taskComponent} />
        ) : null}
      </div>
    </div>
  )
}

PlanItemDetailPanel.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    status: PropTypes.string,
    description: PropTypes.string,
    metadata: PropTypes.object,
    taskIds: PropTypes.array,
    tasks: PropTypes.array
  }).isRequired,
  kind: PropTypes.oneOf(['item', 'category']).isRequired,
  category: PropTypes.shape({
    name: PropTypes.string
  }),
  metadataRows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    accessor: PropTypes.string,
    format: PropTypes.string,
    hideWhenEmpty: PropTypes.bool
  })),
  statusStyleOverrides: PropTypes.object,
  taskComponent: PropTypes.object,
  onClose: PropTypes.func.isRequired
}

PlanItemDetailPanel.defaultProps = {
  metadataRows: []
}

export default PlanItemDetailPanel
